// FilterGroup helpers — build/mutate/serialise filter trees.
// Port skeleton inspired by cts-dashboard_v3/lib/filterLogic.
import type {
  FilterGroup,
  FilterRule,
  RulePath,
} from "../types/inpact";
import type { FieldDef } from "./filterFields";

export function isGroup(node: FilterRule | FilterGroup): node is FilterGroup {
  return (node as FilterGroup).logic !== undefined;
}

export function ruleCount(group: FilterGroup): number {
  return group.rules.reduce(
    (n, r) => n + (isGroup(r) ? ruleCount(r) : 1),
    0,
  );
}

/** Append a top-level rule. Used by FacetCard onChange. */
export function setTopLevelRule(
  group: FilterGroup,
  field: string,
  rule: FilterRule | null,
): FilterGroup {
  const others = group.rules.filter(
    (r) => isGroup(r) || (r as FilterRule).field !== field,
  );
  if (rule == null) return { ...group, rules: others };
  return { ...group, rules: [...others, rule] };
}

/** Empty group helper. */
export function emptyGroup(logic: "AND" | "OR" = "AND"): FilterGroup {
  return { logic, rules: [] };
}

/** Field keys the CRM retired, mapped to what replaced them. Mirrors
 *  `RETIRED_KEYS` in `backend/app/services/field_catalog.py`, which is the
 *  source of truth — only the entries WITH a replacement need to be here,
 *  because only those can be rewritten. */
export const RETIRED_FIELD_KEYS: Record<string, string> = {
  "contact.Country_of_Residence__c": "contact.Country_of_Residence_PL__c",
  "contact.Country_of_birth__c": "contact.Country_of_birth_PL__c",
};

/** Move any retired field key in the tree onto its replacement.
 *
 *  The backend rewrites too, so results are correct either way. This is
 *  about the tree the CLIENT holds: the rail matches its facets by exact
 *  field key, so a retired key sitting in the live filter is invisible to
 *  the facet that owns it — and the next country the user ticks is ANDed
 *  with the one they cannot see, which matches nobody.
 *
 *  Retired keys still arrive from places the backend cannot reach: AnaMoni
 *  answers persisted in localStorage before the migration, and any filter
 *  a user had open across the deploy. Normalising at the store means every
 *  path in is covered, including ones added later.
 */
export function migrateRetiredFields(group: FilterGroup): FilterGroup {
  let changed = false;
  const rules = group.rules.map((r) => {
    if (isGroup(r)) {
      const next = migrateRetiredFields(r);
      if (next !== r) changed = true;
      return next;
    }
    const replacement = RETIRED_FIELD_KEYS[(r as FilterRule).field];
    if (!replacement) return r;
    changed = true;
    return { ...(r as FilterRule), field: replacement };
  });
  return changed ? { ...group, rules } : group;
}

const LEGACY_MULTIPICKLIST_FIELDS = new Set([
  "contact.PwD_Status__c",
  "cert.INPACT_Certification_Types__c",
]);
const LEGACY_MULTIPICKLIST_OPERATORS: Record<string, FilterRule["op"]> = {
  "=": "INCLUDES",
  EQUALS: "INCLUDES",
  EQ: "INCLUDES",
  "!=": "EXCLUDES",
  "NOT EQUALS": "EXCLUDES",
  NEQ: "EXCLUDES",
  IN: "INCLUDES",
  "NOT IN": "EXCLUDES",
  INCLUDES: "INCLUDES",
  EXCLUDES: "EXCLUDES",
};

/** Upgrade filter-schema v1/v2 multipicklist rules returned by an old backend
 *  instance or retained in React Query during a rolling v3 deployment. */
export function migrateLegacyMultipicklistOperators(group: FilterGroup): FilterGroup {
  let changed = false;
  const rules = group.rules.map((node) => {
    if (isGroup(node)) {
      const migrated = migrateLegacyMultipicklistOperators(node);
      if (migrated !== node) changed = true;
      return migrated;
    }
    if (!LEGACY_MULTIPICKLIST_FIELDS.has(node.field)) return node;
    const normalized = node.op.trim().toUpperCase().replace(/_/g, " ");
    const op = LEGACY_MULTIPICKLIST_OPERATORS[normalized];
    if (!op) return node;
    const value = Array.isArray(node.value)
      ? node.value.map(String)
      : typeof node.value === "string" ? [node.value] : null;
    if (value === null) return node;
    changed = changed || op !== node.op || value !== node.value;
    return { ...node, op, value };
  });
  return changed ? { ...group, rules } : group;
}

/** Normalise a rule value into a string array for multi-select facets. The
 *  planner emits picklist selections as either an array or a single scalar
 *  string; both collapse to the same checkbox state. */
export function ruleValueToStringArray(value: FilterRule["value"]): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

/** Find the first top-level rule for a field (helper for facet UI). */
export function findTopLevelRule(
  group: FilterGroup,
  field: string,
): FilterRule | null {
  for (const r of group.rules) {
    if (!isGroup(r) && r.field === field) return r;
  }
  return null;
}

/** A NOT the caller just emptied. Meaningless on its own, and the backend
 *  validator rejects it outright (a NOT must wrap exactly one child), so
 *  leaving one behind would let the chip bar hand /search a filter the app
 *  itself 422s on. */
function isEmptyNot(node: FilterRule | FilterGroup): boolean {
  return isGroup(node) && node.logic === "NOT" && node.rules.length === 0;
}

/** Remove a node by path (used by ActiveFilterChips).
 *
 *  Removing the only child of a NOT normalises that NOT away: a nested one is
 *  dropped from its parent, and one at the root becomes the empty AND filter.
 *  This does NOT move anyone's results — `filter_engine.evaluate` short-circuits
 *  an empty group before it ever looks at the logic (`if not children: return
 *  logic != "OR"`), so an empty NOT and an empty AND both already matched
 *  everything. Only the shape changes, from one the validator rejects to the
 *  one the rest of the app uses for "no filters".
 *
 *  Empty AND and OR groups are left alone — both are valid, long-standing
 *  states (AND matches everything, OR matches nothing). */
export function removeAtPath(group: FilterGroup, path: RulePath): FilterGroup {
  const pruned = removeAtPathInner(group, path);
  return isEmptyNot(pruned) ? { logic: "AND", rules: [] } : pruned;
}

function removeAtPathInner(group: FilterGroup, path: RulePath): FilterGroup {
  if (path.length === 0) return group;
  if (path.length === 1) {
    const idx = path[0];
    return { ...group, rules: group.rules.filter((_, i) => i !== idx) };
  }
  const [head, ...rest] = path;
  const rules: Array<FilterRule | FilterGroup> = [];
  group.rules.forEach((r, i) => {
    if (i !== head || !isGroup(r)) {
      rules.push(r);
      return;
    }
    const next = removeAtPathInner(r, rest);
    if (!isEmptyNot(next)) rules.push(next);
  });
  return { ...group, rules };
}

/** Flatten to top-level rules (drops nested groups). Used by the per-field
 *  facets that only care about their own flat rule. ActiveFilterChips renders
 *  nested groups separately (via describeGroup) so they stay removable. */
export function topLevelRules(group: FilterGroup): FilterRule[] {
  return group.rules.filter((r): r is FilterRule => !isGroup(r));
}

/** Count leaf rules anywhere in a group tree. */
export function countLeafRules(group: FilterGroup): number {
  return group.rules.reduce(
    (n, r) => n + (isGroup(r) ? countLeafRules(r) : 1),
    0,
  );
}

/** Short label for a nested-group chip in the active-filters bar. */
export function describeGroup(group: FilterGroup): string {
  const n = countLeafRules(group);
  return `${n} condition${n === 1 ? "" : "s"} (${group.logic})`;
}

/** Human-readable label for each known field path — used ONLY while the
 *  catalogue (`GET /api/inpact/fields`, the actual source of truth — see
 *  `field_catalog.py`'s `LABEL_OVERRIDES`) hasn't loaded yet, or for a key
 *  the catalogue genuinely never offers (a pinned field like
 *  `contact.INPACT_Inactive_Contact__c`, or a retired key with no
 *  replacement). Every surface that renders a field label should prefer the
 *  live catalogue via `labelsFromFields`/`fieldLabel(field, labels)` and only
 *  fall through to this map — kept as a complete list, not trimmed to "only
 *  the keys the catalogue can't provide", so a slow/failed fetch still shows
 *  a real label instead of an SF api name for every field the app already
 *  knows about.
 */
const FALLBACK_FIELD_LABELS: Record<string, string> = {
  "contact.INPACT_Associate_Status__c": "Associate Status",
  "contact.Country_of_Residence_PL__c": "Country of Residence",
  "contact.Country_of_birth_PL__c": "Country of birth",
  // The text fields those picklists replaced (2026-07-24). Nothing in the
  // app emits them any more, but a saved view or an AnaMoni answer still
  // can, and a chip must never fall back to showing an SF api name.
  "contact.Country_of_Residence__c": "Country of Residence",
  "contact.Country_of_birth__c": "Country of birth",
  "contact.INPACT_Country_Lead__c": "Country Lead",
  "contact.INPACT_Leading_Team__c": "Leading Team",
  "contact.INPACT_Inactive_Contact__c": "Inactive",
  "contact.PwD_Status__c": "Contact role",
  "contact.Education__c": "Education",
  "contact.Date_of_birth__c": "Date of birth",
  "contact.Date_of_diagnosis__c": "Date of diagnosis",
  "contact.Date_of_application__c": "Application date",
  // SF API name is `Date_of_approval__c` for historical reasons — the
  // business meaning is "Date of Certification" (when the Associate
  // completed 1st Level Certification). Confirmed by Mónica 2026-05-05.
  "contact.Date_of_approval__c": "Date of Certification",
  "cert.INPACT_Certification_Types__c": "Certifications",
  "opp.StageName": "Opportunity stage",
  "opp.CloseDate": "Close date",
  "derived.age": "Age",
  "derived.age_at_diagnosis": "Age at diagnosis",
  // v1.2
  "contact.T1D_Stage__c": "T1D Stage (own)",
  "child.T1D_Stage__c": "T1D Stage (child)",
  "derived.has_t1d_child": "Has child with T1D",
  "child.age": "Child age",
  "child.age_at_diagnosis": "Child age at diagnosis",
  "derived.cert_count_first_level": "1st-level cert count",
  "derived.cert_count_second_level": "2nd-level cert count",
  "derived.cert_count_project_specific": "Project-specific cert count",
  "contact.Familiar_Clinical_Trial_Docs__c": "Familiar with clinical trial documents",
  "contact.Reviewed_Clinical_Trial_Docs__c": "Has reviewed clinical trial documents",
  "contact.Participated_Clinical_Trial__c": "Has participated in a clinical trial",
  "derived.has_clinical_experience": "Has clinical trial experience",
};

/** Build a `field -> label` lookup from the fetched catalogue. Cheap enough
 *  to call on every render (a handful of entries); callers that render many
 *  chips in one pass should still memoise this once rather than per-chip. */
export function labelsFromFields(fields: FieldDef[]): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const f of fields) labels[f.key] = f.label;
  return labels;
}

export function fieldLabel(field: string, catalogueLabels?: Record<string, string>): string {
  return catalogueLabels?.[field]
    ?? FALLBACK_FIELD_LABELS[field]
    ?? (field.split(".").slice(1).join(".") || field);
}

/** Pretty-print a rule for the active-filter chip. */
export function describeRule(rule: FilterRule, catalogueLabels?: Record<string, string>): string {
  const label = fieldLabel(rule.field, catalogueLabels);
  // Op tokens come from a few sources (planner emits =/IN/INCLUDES, the
  // legacy SWR snippet emitted equals/in/contains). Normalise once.
  const op = String(rule.op).toLowerCase().replace(/_/g, " ");
  switch (op) {
    case "=":
    case "equals":
      if (typeof rule.value === "boolean") {
        return `${label}: ${rule.value ? "Yes" : "No"}`;
      }
      return `${label}: ${formatValue(rule.value)}`;
    case "!=":
    case "not equals":
      return `${label} ≠ ${formatValue(rule.value)}`;
    case "in":
    case "includes":
      return `${label}: ${formatValue(rule.value)}`;
    case "not in":
    case "excludes":
      return `${label} ∉ ${formatValue(rule.value)}`;
    case "contains":
      return `${label} contains "${formatValue(rule.value)}"`;
    case "starts with":
      return `${label} starts with "${formatValue(rule.value)}"`;
    case "between": {
      const v = rule.value as [string, string];
      return `${label}: ${v[0]} – ${v[1]}`;
    }
    case "is null":
    case "is empty":
      return `${label} is empty`;
    case "is not null":
    case "is not empty":
      return `${label} is not empty`;
    case ">":
    case "gt":
      return `${label} > ${formatValue(rule.value)}`;
    case "<":
    case "lt":
      return `${label} < ${formatValue(rule.value)}`;
    case ">=":
    case "gte":
      return `${label} ≥ ${formatValue(rule.value)}`;
    case "<=":
    case "lte":
      return `${label} ≤ ${formatValue(rule.value)}`;
    default:
      return label;
  }
}

function formatValue(v: FilterRule["value"]): string {
  if (Array.isArray(v)) return v.map(String).join(", ");
  if (v === null) return "";
  return String(v);
}

/** Mirrors backend/app/services/filter_validation.py MAX_FILTER_DEPTH. A tree
 *  deeper than this is rejected with a 422 by every filter endpoint. */
export const MAX_FILTER_DEPTH = 20;

/** Deepest node depth in a tree, counted exactly the way the backend counts it
 *  (`filter_validation.validate_node`): the root is depth 0 and every child —
 *  including a NOT wrapper, which is itself a node — is one deeper. The builder
 *  renders NOT wrappers transparently, so its visual nesting level is NOT this
 *  number; use this whenever the question is "will the backend accept it?". */
export function maxNodeDepth(node: FilterRule | FilterGroup, depth = 0): number {
  if (!isGroup(node) || node.rules.length === 0) return depth;
  return Math.max(...node.rules.map((r) => maxNodeDepth(r, depth + 1)));
}

/** Split a node into "the thing being negated" and whether it is negated.
 *
 *  A node is *negatable* only when it is a NOT wrapping exactly one child that
 *  is not itself a NOT. Anything else — a multi-child NOT (which the engine
 *  evaluates partially: it negates children[0] only) or a double negation — is
 *  reported as un-negated so callers render it read-only and leave the tree
 *  byte-for-byte intact rather than silently normalising it. */
export function unwrapNegation(node: FilterRule | FilterGroup): {
  inner: FilterRule | FilterGroup;
  negated: boolean;
} {
  if (!isGroup(node) || node.logic !== "NOT") return { inner: node, negated: false };
  if (node.rules.length !== 1) return { inner: node, negated: false };
  const child = node.rules[0];
  if (isGroup(child) && child.logic === "NOT") return { inner: node, negated: false };
  return { inner: child, negated: true };
}

/** Plain-English reason `filter_validation.validate_node` would 422 `group`,
 *  or `null` if the backend will accept it as-is. Mirrors the two structural
 *  invariants that validator enforces beyond shape:
 *
 *  - **Depth** — reuses `maxNodeDepth` rather than re-walking the tree, since
 *    that already counts nodes exactly the way the backend does.
 *  - **NOT arity** — the engine only negates `children[0]`, so a NOT wrapping
 *    zero or 2+ children is a partial/broken negation the backend refuses
 *    rather than silently misevaluate. The builder deliberately keeps a NOT
 *    it cannot author (multi-child, empty, or double-negated *at that node*)
 *    read-only so it round-trips byte-for-byte through open → Apply (see
 *    `unwrapNegation`/`toggleNegation`) — this function is what stops the
 *    user from pressing Apply on it anyway, instead of finding out from a
 *    422 after the fact.
 *
 *  Depth is checked first: a tree can fail both checks at once, and only one
 *  reason is ever shown, so the more actionable one (depth, which the "Add
 *  group" gate already mostly prevents) leads. */
export function backendRejectionReason(group: FilterGroup): string | null {
  if (maxNodeDepth(group) > MAX_FILTER_DEPTH) {
    return "This filter has too many nested levels to apply. Remove a few "
      + 'groups or "Not" conditions and try again.';
  }
  if (hasMalformedNot(group)) {
    return 'One of the "Not" conditions in this filter isn\'t wrapping '
      + 'exactly one rule or group, so it can\'t be applied. Remove or fix '
      + 'that "Not" and try again.';
  }
  return null;
}

/** True if any NOT node anywhere in the tree wraps zero or 2+ children —
 *  the one NOT shape the backend structurally rejects (double negation, i.e.
 *  a NOT directly wrapping another NOT, is *not* flagged here: each of those
 *  NOTs still wraps exactly one child, so the backend accepts it fine). */
function hasMalformedNot(node: FilterRule | FilterGroup): boolean {
  if (!isGroup(node)) return false;
  if (node.logic === "NOT" && node.rules.length !== 1) return true;
  return node.rules.some(hasMalformedNot);
}

/** Wrap a node in a NOT, or unwrap it if it already is one.
 *
 *  A well-formed negated node (a NOT wrapping exactly one non-NOT child) unwraps
 *  instead of adding a second NOT wrapper. A malformed NOT (multi-child, empty,
 *  or double-negated) is returned unchanged: toggling it would silently deepen a
 *  user's saved filter, violating the standing invariant that this function only
 *  adds or removes a single layer. This keeps the tree byte-for-byte intact and
 *  lets the caller render such nodes read-only.
 *
 *  The result is that a NOT group produced by this function always wraps exactly
 *  one child and is never itself a NOT (double negation is unreachable). */
export function toggleNegation(
  node: FilterRule | FilterGroup,
): FilterRule | FilterGroup {
  const { inner, negated } = unwrapNegation(node);
  if (negated) return inner;
  // A NOT that unwrapNegation refused to unwrap is malformed (multi-child,
  // empty, or a double negation). Wrapping it would deepen someone's saved
  // filter behind their back — return it untouched and let the caller render
  // it read-only.
  if (isGroup(node) && node.logic === "NOT") return node;
  return { logic: "NOT", rules: [node] };
}
